import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutline as CheckCircleOutlineIcon } from '@mui/icons-material';
import { Link as RouterLink, useOutletContext, useParams } from 'react-router-dom';
import api from '../../api';
import { PORTAL_LINK_UNAVAILABLE, portalTokenErrorMessage } from './portalTokenMessages';

/**
 * ZRF-WTPB-010 — mobile-first access confirm / decline / reschedule.
 * Scope from token only — never send property/tenant overrides.
 */
const PortalAccessForm = () => {
  const { eventId } = useParams();
  const { token, tokenHint, validation, validationLoading, validationError } =
    useOutletContext() || {};

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  const scopeError =
    validation && validation.valid
      ? portalTokenErrorMessage(validation, { requiredActionType: 'confirm_access' })
      : null;

  const linkUnavailable =
    Boolean(validationError) ||
    (validation && validation.valid === false) ||
    Boolean(scopeError);

  const unavailableMessage =
    validationError ||
    (validation && validation.valid === false
      ? portalTokenErrorMessage(validation)
      : null) ||
    scopeError ||
    PORTAL_LINK_UNAVAILABLE;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token || linkUnavailable) {
        setSummaryLoading(false);
        return;
      }
      setSummaryLoading(true);
      setSummaryError('');
      try {
        const path = eventId
          ? `/api/portal/tokens/${encodeURIComponent(token)}/access/${encodeURIComponent(eventId)}`
          : `/api/portal/tokens/${encodeURIComponent(token)}/access`;
        const res = await api.get(path);
        if (cancelled) return;
        if (res.data?.valid === false) {
          setSummaryError(portalTokenErrorMessage(res.data) || PORTAL_LINK_UNAVAILABLE);
          setSummary(null);
          return;
        }
        setSummary(res.data?.access || null);
        console.log('🔍 portal access loaded', {
          accessEventId: res.data?.access?.accessEventId,
          tokenHint,
        });
      } catch (err) {
        console.error('❌ portal access load failed:', err);
        if (!cancelled) {
          const data = err?.response?.data;
          setSummaryError(portalTokenErrorMessage(data) || PORTAL_LINK_UNAVAILABLE);
        }
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token, eventId, linkUnavailable, tokenHint]);

  const submitResponse = async (response) => {
    setSubmitError('');
    if (!token || linkUnavailable) {
      setSubmitError(PORTAL_LINK_UNAVAILABLE);
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/api/portal/tokens/${encodeURIComponent(token)}/access-response`,
        {
          response,
          note: note.trim() || undefined,
          // Do NOT send propertyId / tenantId / accessEventId as authoritative
        }
      );
      if (res.data?.valid === false) {
        setSubmitError(portalTokenErrorMessage(res.data) || PORTAL_LINK_UNAVAILABLE);
        return;
      }
      setConfirmation({
        responseStatus: res.data?.responseStatus || response,
        respondedAt: res.data?.respondedAt,
        accessEventId: res.data?.accessEventId,
      });
      console.log('✅ portal access response', {
        responseStatus: res.data?.responseStatus,
        accessEventId: res.data?.accessEventId,
      });
    } catch (err) {
      console.error('❌ portal access response failed:', err);
      const data = err?.response?.data;
      setSubmitError(
        portalTokenErrorMessage(data) ||
          data?.error?.message ||
          data?.error ||
          'Could not submit your response. Please try again or ask your landlord for a new link.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (validationLoading || summaryLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary">
          Loading access request…
        </Typography>
      </Stack>
    );
  }

  if (linkUnavailable || summaryError) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" component="h1">
          Link unavailable
        </Typography>
        <Alert severity="warning">{summaryError || unavailableMessage}</Alert>
      </Stack>
    );
  }

  if (confirmation) {
    const label =
      confirmation.responseStatus === 'confirmed'
        ? 'Access confirmed'
        : confirmation.responseStatus === 'declined'
          ? 'Access declined'
          : 'Reschedule requested';
    return (
      <Stack spacing={2} alignItems="flex-start">
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
        <Typography variant="h5" component="h1">
          {label}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your landlord has been notified
          {confirmation.respondedAt
            ? ` · ${new Date(confirmation.respondedAt).toLocaleString()}`
            : ''}
          .
        </Typography>
        <Button
          component={RouterLink}
          to={`/p/t/${encodeURIComponent(token || '')}`}
          variant="outlined"
        >
          Back
        </Button>
      </Stack>
    );
  }

  const start = summary?.scheduledStartAt
    ? new Date(summary.scheduledStartAt).toLocaleString()
    : null;
  const end = summary?.scheduledEndAt
    ? new Date(summary.scheduledEndAt).toLocaleString()
    : null;

  return (
    <Stack spacing={2.5} component="form" onSubmit={(e) => e.preventDefault()}>
      <Typography variant="h5" component="h1">
        {summary?.title || 'Access request'}
      </Typography>
      {summary?.description ? (
        <Typography variant="body1" color="text.secondary">
          {summary.description}
        </Typography>
      ) : null}
      {(start || end) && (
        <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
          {start ? `Proposed: ${start}` : null}
          {start && end ? ' – ' : null}
          {end && start ? end : end ? `Until ${end}` : null}
        </Alert>
      )}
      {!start && !end && summary?.whenText ? (
        <Alert severity="info" sx={{ alignItems: 'flex-start' }}>
          {`When: ${summary.whenText}`}
        </Alert>
      ) : null}
      <TextField
        label="Optional note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        placeholder="e.g. Confirmed, or suggest another time"
      />
      {submitError ? <Alert severity="error">{submitError}</Alert> : null}
      <Stack spacing={1.5}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={submitting}
          onClick={() => submitResponse('confirmed')}
          sx={{ py: 1.5 }}
        >
          Confirm access
        </Button>
        <Button
          variant="outlined"
          size="large"
          fullWidth
          disabled={submitting}
          onClick={() => submitResponse('reschedule_requested')}
        >
          Request a different time
        </Button>
        <Button
          color="inherit"
          size="large"
          fullWidth
          disabled={submitting}
          onClick={() => submitResponse('declined')}
        >
          Decline
        </Button>
      </Stack>
      {submitting ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress size={28} />
        </Box>
      ) : null}
    </Stack>
  );
};

export default PortalAccessForm;
